import { checkIsValidSchedule, getCurrentTimezone } from '../utils';
import moment from 'moment-timezone';
import * as S from './style';
import ClockIcon from '@svgIcons/clock.svg';
import TimePicker from './TimePicker';

const HOUR_IN_MINUTES = 60;

export const ScheduleItem = {
    name: 'ScheduleItem',
    props: {
        value: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            isTimePickerOpen: false,
        };
    },
    computed: {
        isValidSchedule() {
            return checkIsValidSchedule(this.value);
        },
        hourInUTC() {
            // we save UTC hour in our database
            return this.value.hour ?? new Date().getUTCHours();
        },
        minuteInUTC() {
            // we save UTC minute in our database
            return this.value.minute ?? 0;
        },
        utcMoment() {
            const { hourInUTC, minuteInUTC } = this;

            const utcMoment = moment.utc();
            utcMoment.set('hour', hourInUTC);
            utcMoment.set('minute', minuteInUTC);

            return utcMoment;
        },
        localMoment() {
            const { utcMoment } = this;
            const timezone = getCurrentTimezone();
            return utcMoment.clone().tz(timezone);
        },
        hourInLocalTime() {
            const { localMoment } = this;
            return localMoment.get('hour');
        },
        minuteInLocalTime() {
            const { utcMoment } = this;
            const timezone = getCurrentTimezone();
            const localMoment = utcMoment.clone().tz(timezone);

            return localMoment.get('minute');
        },
        timezoneOffset() {
            const timezone = getCurrentTimezone();
            const offsetInMinutes = moment.tz(timezone).utcOffset();

            if (offsetInMinutes === 0) {
                return '(UTC)';
            }

            const hours = Math.floor(
                Math.abs(offsetInMinutes) / HOUR_IN_MINUTES
            );
            const minutes = Math.abs(offsetInMinutes) % HOUR_IN_MINUTES;
            const sign = offsetInMinutes >= 0 ? '+' : '-';
            return `(UTC ${sign}${hours}${minutes ? `:${minutes}` : ''})`;
        },
    },
    methods: {
        toggleTimePicker() {
            this.isTimePickerOpen = !this.isTimePickerOpen;
        },
        handleChange(valueInLocalTime) {
            const { hour, minute } = valueInLocalTime;

            const timezone = getCurrentTimezone();
            const localMoment = moment().tz(timezone);
            localMoment.set('hour', hour);
            localMoment.set('minute', minute);

            const utcMoment = localMoment.utc();

            const schedule = {
                hour: utcMoment.get('hour'),
                minute: utcMoment.get('minute'),
            };

            this.isTimePickerOpen = false;

            this.$emit('change', schedule);
        },
        handleClickOutside(e) {
            const isClickInside = this.$el.contains(e.target);

            if (this.isTimePickerOpen && !isClickInside) {
                this.toggleTimePicker();
            }
        },
    },
    mounted() {
        document.addEventListener('click', this.handleClickOutside);
    },
    beforeDestroy() {
        document.removeEventListener('click', this.handleClickOutside);
    },
    render() {
        const {
            hourInLocalTime,
            minuteInLocalTime,
            isValidSchedule,
            isTimePickerOpen,
        } = this;

        return (
            <S.ScheduleItemContainer>
                <S.ScheduleItemWrapper
                    onClick={this.toggleTimePicker}
                    isActive={isTimePickerOpen}
                >
                    {isValidSchedule ? (
                        <S.TimeWrapper>
                            <S.TimeText>
                                {String(hourInLocalTime).padStart(2, '0')}
                                {' : '}
                                {String(minuteInLocalTime).padStart(2, '0')}
                            </S.TimeText>
                            <S.TimezoneText>
                                {this.timezoneOffset}
                            </S.TimezoneText>
                        </S.TimeWrapper>
                    ) : (
                        <S.PlaceholderText>
                            {this.$t('EXPORT_DATA.schedule_placeholder')}
                        </S.PlaceholderText>
                    )}

                    <ClockIcon width="24" height="24" v-view-box />
                </S.ScheduleItemWrapper>
                {isTimePickerOpen && (
                    <TimePicker
                        hour={hourInLocalTime}
                        minute={minuteInLocalTime}
                        onChange={this.handleChange}
                        onClose={() => {
                            this.isTimePickerOpen = false;
                        }}
                    />
                )}
            </S.ScheduleItemContainer>
        );
    },
};

export default ScheduleItem;
